<script lang="ts">
	import SvelteSeo from 'svelte-seo';
		import { page } from '$app/stores';
	import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
	import { Button } from '$lib/components/ui/button';
	import { Card, CardContent } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import {
		CheckCircle,
		X,
		DollarSign,
		Zap,
		Clock,
		CloudUpload,
		TrendingUp,
		Target
	} from '@lucide/svelte';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	let currentTestimonial = $state(0);

	const testimonials = [
		{
			company: 'TechCorp',
			role: 'VP of Engineering',
			quote:
				'We hired 3 senior developers in 2 weeks. Traditional recruiters took us 3 months for worse candidates.',
			metric: '85% faster'
		},
		{
			company: 'StartupXYZ',
			role: 'CTO',
			quote:
				'Finally, a job board that understands tech. No more QA engineers applying for architect roles.',
			metric: '92% match accuracy'
		},
		{
			company: 'DevShop Inc',
			role: 'Head of Talent',
			quote: 'Saved $45K in recruiter fees per hire. The AI matching actually works.',
			metric: '$45K saved per hire'
		}
	];

	function scrollToJdDrop() {
		const section = document.getElementById('jd-drop-section');
		if (section) {
			section.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	// Testimonial rotation
	$effect(() => {
		const interval = setInterval(() => {
			currentTestimonial = (currentTestimonial + 1) % testimonials.length;
		}, 5000);

		return () => clearInterval(interval);
	});

	const seo = createPageSEO({
		title: 'Hire Top Tech Talent Fast - TalentProfile for Employers',
		description:
			'Skip traditional ATS headaches. Drop your JD, get qualified developers in 24h. 28,000+ pre-screened candidates. 100% free forever.',
		canonical: '/hire',
		keywords:
			'hire developers, tech recruitment, ATS alternative, developer hiring, engineering recruitment',
		ogImage: '/hire.png',
		ogType: 'website',
		structuredData: createWebPageSchema({
			name: 'Hire Developers - TalentProfile',
			description: 'AI-powered developer hiring platform',
			url: '/hire'
		})
	});
</script>

{#key $page.url.pathname}
  <SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white to-gray-50">
	<!-- Hero Section with Interactive JD Drop -->
	<section class="container mx-auto px-4 py-16 md:py-20">
		<div class="mx-auto max-w-7xl">
			<div class="grid items-center gap-12 lg:grid-cols-2">
				<!-- Left: Copy -->
				<div>
					<Badge class="mb-4" variant="secondary">
						<Zap class="mr-1 h-4 w-4" />
						No ATS BS • 24h First Matches • 100% Free
					</Badge>

					<h1 class="mb-6 text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
						Hire Senior Devs
						<span class="text-blue-600"> Without The ATS Hell</span>
					</h1>

					<p class="mb-8 text-xl leading-relaxed text-gray-600">
						Traditional ATS makes you fill 47 fields. We make you drop one file.
						<strong>First qualified matches arrive in 24 hours.</strong>
					</p>

					<div class="mb-8 flex flex-col gap-4 sm:flex-row">
						<Button size="lg" class="text-lg" onclick={scrollToJdDrop}>
							<CloudUpload class="mr-2 h-5 w-5" />
							Drop JD & Start Hiring
						</Button>
						<Button size="lg" variant="outline" class="text-lg" href="#how-it-works">
							Watch 60s Demo
						</Button>
					</div>

					<div class="grid grid-cols-3 gap-4 text-center">
						<div>
							<div class="text-3xl font-bold text-blue-600">28K+</div>
							<div class="text-xs text-gray-600">Pre-screened devs</div>
						</div>
						<div>
							<div class="text-3xl font-bold text-blue-600">24h</div>
							<div class="text-xs text-gray-600">First matches</div>
						</div>
						<div>
							<div class="text-3xl font-bold text-blue-600">$0</div>
							<div class="text-xs text-gray-600">Forever free</div>
						</div>
					</div>
				</div>

				<!-- Right: Interactive JD Drop -->
				<div class="relative" id="jd-drop-section">
					<Card
						class="relative overflow-hidden border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50"
					>
						<CardContent>
							<div class="space-y-6 py-8 text-center">
								<div
									class="mx-auto flex h-20 w-20 animate-pulse items-center justify-center superellipse bg-blue-100"
								>
									<CloudUpload class="h-10 w-10 text-blue-600" />
								</div>

								<div>
									<h3 class="mb-2 text-2xl font-bold text-gray-900">Drop Your Job Description</h3>
									<p class="text-gray-600">
										PDF, DOC, or paste text • We extract everything automatically
									</p>
								</div>

								<Button size="lg" class="w-full" href="/drop-jd">
									<CloudUpload class="mr-2 h-5 w-5" />
									Choose File or Drop Here
								</Button>

								<!-- Live stats ticker -->
								<div class="space-y-2 border-t pt-4 text-sm">
									<div class="flex items-center justify-between text-gray-600">
										<span class="flex items-center gap-2">
											<span class="h-2 w-2 animate-pulse superellipse bg-green-500"></span>
											<span>17 companies posted today</span>
										</span>
									</div>
									<div class="flex items-center justify-between text-gray-600">
										<span class="flex items-center gap-2">
											<span class="h-2 w-2 animate-pulse superellipse bg-green-500"></span>
											<span>243 developers matched this week</span>
										</span>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<!-- Floating social proof badges -->
					<div
						class="absolute -top-4 -right-4 animate-bounce rounded-full bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
					>
						100% Free
					</div>
					<div
						class="absolute -bottom-4 -left-4 rounded-full bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
					>
						No Credit Card Required
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Rotating Testimonials Bar -->
	<section class="bg-gradient-to-r from-blue-600 to-purple-600 py-6">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-4xl text-center text-white">
				<div class="transition-all duration-500">
					<p class="mb-2 text-lg italic">"{testimonials[currentTestimonial].quote}"</p>
					<div class="flex items-center justify-center gap-3 text-sm opacity-90">
						<span class="font-semibold">{testimonials[currentTestimonial].company}</span>
						<span>•</span>
						<span>{testimonials[currentTestimonial].role}</span>
						<span>•</span>
						<Badge variant="secondary" class="border-0 bg-white/20 text-white">
							{testimonials[currentTestimonial].metric}
						</Badge>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Why Traditional ATS Fails - Enhanced Visual Comparison -->
	<section id="benefits" class="container mx-auto px-4 py-20">
		<div class="mb-12 text-center">
			<Badge class="mb-4" variant="secondary">The Truth</Badge>
			<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
				Why Traditional ATS is Broken for Tech Hiring
			</h2>
			<p class="mx-auto max-w-2xl text-lg text-gray-600">
				Built by developers who got frustrated with keyword-matching garbage
			</p>
		</div>

		<div class="mx-auto max-w-6xl">
			<!-- Visual comparison grid -->
			<div class="mb-12 grid gap-8 md:grid-cols-2">
				<!-- Traditional ATS Pain Points -->
				<div class="space-y-4">
					<div class="mb-6 flex items-center gap-3">
						<div class="flex h-10 w-10 items-center justify-center superellipse bg-red-100">
							<X class="h-6 w-6 text-red-600" />
						</div>
						<h3 class="text-2xl font-bold text-red-600">Traditional ATS</h3>
					</div>

					<Card class="border-red-200 bg-red-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">😤</div>
								<div>
									<h4 class="mb-2 font-semibold text-red-900">47 Required Form Fields</h4>
									<p class="text-sm text-red-800">
										"Please re-enter everything from your JD into our 1990s interface. Then do it
										again for each job board."
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-red-200 bg-red-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">🤦</div>
								<div>
									<h4 class="mb-2 font-semibold text-red-900">Keyword Matching Only</h4>
									<p class="text-sm text-red-800">
										QA Engineer with "Python" gets matched to Python Backend Architect role. Because
										keywords.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-red-200 bg-red-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">💸</div>
								<div>
									<h4 class="mb-2 font-semibold text-red-900">$500+/month Subscription</h4>
									<p class="text-sm text-red-800">
										Plus recruiter fees. Plus job board fees. Plus integration fees. It never ends.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-red-200 bg-red-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">🐌</div>
								<div>
									<h4 class="mb-2 font-semibold text-red-900">2-3 Months to First Hire</h4>
									<p class="text-sm text-red-800">
										Post job. Wait. Sift through 500 garbage applications. Interview 3 qualified
										people. Start over.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				<!-- TalentProfile Advantages -->
				<div class="space-y-4">
					<div class="mb-6 flex items-center gap-3">
						<div class="flex h-10 w-10 items-center justify-center superellipse bg-green-100">
							<CheckCircle class="h-6 w-6 text-green-600" />
						</div>
						<h3 class="text-2xl font-bold text-green-600">TalentProfile</h3>
					</div>

					<Card class="border-green-200 bg-green-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">⚡</div>
								<div>
									<h4 class="mb-2 font-semibold text-green-900">Drop JD. Done.</h4>
									<p class="text-sm text-green-800">
										AI extracts job title, requirements, skills, everything. 2 minutes total. Never
										touch a form field.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-green-200 bg-green-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">🎯</div>
								<div>
									<h4 class="mb-2 font-semibold text-green-900">3-Layer AI Matching</h4>
									<p class="text-sm text-green-800">
										Role first. Then skills. Then AI verification. Only Backend Engineers see
										Backend roles. Shocking concept.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-green-200 bg-green-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">🎉</div>
								<div>
									<h4 class="mb-2 font-semibold text-green-900">$0 Forever</h4>
									<p class="text-sm text-green-800">
										Unlimited jobs. Unlimited hires. No subscriptions, no recruiter fees, no
										bullshit. Actually free.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-green-200 bg-green-50">
						<CardContent>
							<div class="flex items-start gap-3">
								<div class="text-4xl">🚀</div>
								<div>
									<h4 class="mb-2 font-semibold text-green-900">First Matches in 24 Hours</h4>
									<p class="text-sm text-green-800">
										Drop JD Monday morning. Review qualified candidates Tuesday afternoon. Interview
										starts Wednesday.
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			<!-- Bottom Impact Statement -->
			<Card class="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
				<CardContent class="text-center">
					<TrendingUp class="mx-auto mb-4 h-12 w-12 text-blue-600" />
					<h4 class="mb-3 text-2xl font-bold text-blue-900">
						Built by Developers, For Hiring Developers
					</h4>
					<p class="mx-auto max-w-2xl text-blue-800">
						We spent years applying through broken ATS systems. Then we spent more years hiring
						through the same broken systems. So we built something that actually works. No MBA
						product managers involved.
					</p>
				</CardContent>
			</Card>
		</div>
	</section>

	<!-- How It Works - More Visual -->
	<section id="how-it-works" class="bg-gray-50 py-20">
		<div class="container mx-auto px-4">
			<div class="mb-12 text-center">
				<Badge class="mb-4" variant="secondary">The Process</Badge>
				<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
					From JD to Hire in Days, Not Months
				</h2>
			</div>

			<div class="mx-auto max-w-5xl">
				<!-- Timeline visualization -->
				<div class="relative">
					<!-- Connecting line -->
					<div
						class="absolute top-0 bottom-0 left-1/2 hidden w-0.5 -translate-x-1/2 bg-gradient-to-b from-blue-600 via-purple-600 to-green-600 md:block"
					></div>

					<!-- Step 1 -->
					<div class="relative mb-12">
						<div class="items-center gap-8 md:flex">
							<div class="mb-4 md:mb-0 md:w-1/2 md:text-right">
								<Badge class="mb-2">2 Minutes</Badge>
								<h3 class="mb-3 text-2xl font-bold text-gray-900">1. Drop Your JD</h3>
								<p class="leading-relaxed text-gray-600">
									Upload PDF, DOC, or paste text. Our AI extracts everything: title, requirements,
									skills, experience level, location, salary. Zero form fields.
								</p>
							</div>
							<div
								class="z-10 hidden h-12 w-12 flex-shrink-0 items-center justify-center superellipse bg-blue-600 text-xl font-bold text-white md:flex"
							>
								1
							</div>
							<div class="md:w-1/2">
								<Card class="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
									<CardContent>
										<div class="space-y-2 text-sm">
											<div class="flex items-center gap-2">
												<CheckCircle class="h-4 w-4 text-green-600" />
												<span>Job title extracted</span>
											</div>
											<div class="flex items-center gap-2">
												<CheckCircle class="h-4 w-4 text-green-600" />
												<span>5+ years experience detected</span>
											</div>
											<div class="flex items-center gap-2">
												<CheckCircle class="h-4 w-4 text-green-600" />
												<span>Node.js, React, AWS identified</span>
											</div>
											<div class="flex items-center gap-2">
												<CheckCircle class="h-4 w-4 text-green-600" />
												<span>Remote policy: Hybrid</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</div>

					<!-- Step 2 -->
					<div class="relative mb-12">
						<div class="flex-row-reverse items-center gap-8 md:flex">
							<div class="mb-4 md:mb-0 md:w-1/2">
								<Badge class="mb-2">24 Hours</Badge>
								<h3 class="mb-3 text-2xl font-bold text-gray-900">
									2. AI Finds & Notifies Matches
								</h3>
								<p class="leading-relaxed text-gray-600">
									We scan 28K+ developer profiles. Match by role first (no QA for dev jobs). Then
									skills. Then AI verifies. Only 85%+ matches get notified.
								</p>
							</div>
							<div
								class="z-10 hidden h-12 w-12 flex-shrink-0 items-center justify-center superellipse bg-purple-600 text-xl font-bold text-white md:flex"
							>
								2
							</div>
							<div class="md:w-1/2">
								<Card class="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
									<CardContent>
										<div class="space-y-3">
											<div class="flex items-center justify-between text-sm">
												<span>Candidates scanned</span>
												<span class="font-bold text-purple-600">28,491</span>
											</div>
											<div class="flex items-center justify-between text-sm">
												<span>Role matches</span>
												<span class="font-bold text-purple-600">1,247</span>
											</div>
											<div class="flex items-center justify-between text-sm">
												<span>Skill matches</span>
												<span class="font-bold text-purple-600">186</span>
											</div>
											<div class="flex items-center justify-between text-sm">
												<span>AI verified (85%+)</span>
												<span class="font-bold text-green-600">23 ✓</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</div>

					<!-- Step 3 -->
					<div class="relative">
						<div class="items-center gap-8 md:flex">
							<div class="mb-4 md:mb-0 md:w-1/2 md:text-right">
								<Badge class="mb-2">3-5 Days</Badge>
								<h3 class="mb-3 text-2xl font-bold text-gray-900">3. Review, Interview, Hire</h3>
								<p class="leading-relaxed text-gray-600">
									Get notifications as candidates respond. Review GitHub profiles. Schedule
									interviews. Make offers. Average time to hire: 12 days.
								</p>
							</div>
							<div
								class="z-10 hidden h-12 w-12 flex-shrink-0 items-center justify-center superellipse bg-green-600 text-xl font-bold text-white md:flex"
							>
								3
							</div>
							<div class="md:w-1/2">
								<Card class="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
									<CardContent>
										<div class="space-y-2 text-sm">
											<div class="flex justify-between border-b pb-2">
												<span class="text-gray-600">Day 1</span>
												<span class="font-medium">23 candidates notified</span>
											</div>
											<div class="flex justify-between border-b pb-2">
												<span class="text-gray-600">Day 2</span>
												<span class="font-medium">8 expressed interest</span>
											</div>
											<div class="flex justify-between border-b pb-2">
												<span class="text-gray-600">Day 5</span>
												<span class="font-medium">3 first interviews</span>
											</div>
											<div class="flex justify-between">
												<span class="font-semibold text-gray-600">Day 12</span>
												<span class="font-bold text-green-600">Hired! 🎉</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<section id="also-us" class="container mx-auto px-4 py-4">
		<GlopayCtaHorizontal placement="hire-post-how-it-works" />
	</section>

	<!-- Social Proof Section -->
	<section id="proof" class="container mx-auto px-4 py-20">
		<div class="mb-12 text-center">
			<Badge class="mb-4" variant="secondary">Trusted By</Badge>
			<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
				Companies Already Hiring Smarter
			</h2>
		</div>

		<div class="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
			<Card class="border-blue-200">
				<CardContent>
					<div class="mb-4 text-4xl">🚀</div>
					<div class="mb-2 text-2xl font-bold text-blue-600">92%</div>
					<p class="mb-4 text-sm text-gray-600">
						of our matches result in interviews vs. 12% industry average
					</p>
					<div class="flex items-center gap-2 text-xs text-gray-500">
						<Target class="h-3 w-3" />
						<span>Higher quality matching</span>
					</div>
				</CardContent>
			</Card>

			<Card class="border-green-200">
				<CardContent>
					<div class="mb-4 text-4xl">⚡</div>
					<div class="mb-2 text-2xl font-bold text-green-600">12 Days</div>
					<p class="mb-4 text-sm text-gray-600">average time to hire vs. 45 days traditional ATS</p>
					<div class="flex items-center gap-2 text-xs text-gray-500">
						<Clock class="h-3 w-3" />
						<span>3.7x faster hiring</span>
					</div>
				</CardContent>
			</Card>

			<Card class="border-purple-200">
				<CardContent>
					<div class="mb-4 text-4xl">💰</div>
					<div class="mb-2 text-2xl font-bold text-purple-600">$0</div>
					<p class="mb-4 text-sm text-gray-600">vs. $500/mo ATS + $15K recruiter fees per hire</p>
					<div class="flex items-center gap-2 text-xs text-gray-500">
						<DollarSign class="h-3 w-3" />
						<span>Massive cost savings</span>
					</div>
				</CardContent>
			</Card>
		</div>
	</section>

	<!-- Final CTA -->
	<section class="container mx-auto px-4 py-20">
		<div
			class="mx-auto max-w-3xl rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white"
		>
			<h2 class="mb-4 text-3xl font-bold md:text-4xl">Stop Fighting Your ATS</h2>
			<p class="mb-8 text-lg opacity-90">
				Drop your JD. Get qualified devs. Make offers. It's that simple.
			</p>
			<div class="flex flex-col items-center justify-center gap-4 sm:flex-row">
				<Button size="lg" variant="secondary" class="px-8 text-lg" href="/drop-jd">
					<CloudUpload class="mr-2 h-5 w-5" />
					Drop Your JD Now
				</Button>
				<Button
					size="lg"
					href="/contact"
					variant="outline"
					class="border-white bg-transparent px-8 text-lg text-white hover:bg-white hover:text-blue-600"
				>
					Talk to Founders
				</Button>
			</div>
			<p class="mt-6 text-sm opacity-75">
				2 min setup • First matches in 24h • Free forever • No credit card required
			</p>
		</div>
	</section>
</div>
